<template>
  <div v-if="loading && !hire" class="tw-flex tw-justify-center tw-py-8" data-test="hire-panel-overview-loading">
    <WdsSpinner :size="24" indeterminate />
  </div>

  <WdsAlert v-else-if="error" error data-test="hire-panel-overview-error">
    {{ $t('The hire details could not be loaded. Try refreshing the page.') }}
  </WdsAlert>

  <WdsStack v-else-if="hire" direction-column gap-md>
    <WdsStack
      direction-column
      gap-sm
      class="tw-rounded tw-border tw-border-solid tw-border-gray-200 tw-bg-white tw-p-3.5"
    >
      <HirePanelFact :label="$t('Contract status')" data-test="hire-panel-fact-contract-status">
        <WdsLabel
          sm
          :gray="contractStatus.gray"
          :success="contractStatus.success"
          :error="contractStatus.error"
          :warning="contractStatus.warning"
          :subtle="contractStatus.subtle"
          :label="contractStatus.text"
        />
      </HirePanelFact>

      <HirePanelFact v-if="hire.endClient" :label="$t('Client')" data-test="hire-panel-fact-client">
        <WdsStack align-center gap-sm>
          <WdsAvatar :path="getAvatarPath(hire.endClient.avatar, hire.endClient.id)" xs />
          <WdsText sm>{{ hire.endClient.name }}</WdsText>
        </WdsStack>
      </HirePanelFact>

      <HirePanelFact :label="$t('Engagement type')" data-test="hire-panel-fact-engagement-type">
        <HireEngagement :row="hire" stack-horizontally sm />
      </HirePanelFact>

      <HirePanelFact
        v-if="contract?.location?.formattedAddress"
        :label="$t('Work address')"
        data-test="hire-panel-fact-address"
      >
        <WdsText sm class="tw-text-right">{{ contract.location.formattedAddress }}</WdsText>
      </HirePanelFact>

      <HirePanelFact
        v-if="contract?.locationPreference"
        :label="$t('Primary work location')"
        data-test="hire-panel-fact-location-type"
      >
        <WdsTag gray sm>{{ locationPreferenceLabel(contract.locationPreference) }}</WdsTag>
      </HirePanelFact>
    </WdsStack>

    <HirePanelCustomFields :hire-id="hireId" :answered-by="customFieldsAnsweredBy" />
  </WdsStack>

  <WdsText v-else secondary data-test="hire-panel-overview-not-found">
    {{ $t('This hire could not be found.') }}
  </WdsText>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import {
  WdsAlert,
  WdsAvatar,
  WdsLabel,
  WdsSpinner,
  WdsStack,
  WdsTag,
  WdsText,
} from '@worksome/wds-components'
import HireEngagement from '@/components/GraphqlDataTable/columns/Hire/HireEngagement.vue'
import { useHireContract } from '@/components/Hire/composables/useHireContract'
import { HireCustomFieldsAnsweredBy } from '@/components/Hire/composables/useHireCustomFieldSections'
import HirePanelCustomFields from '@/components/Hire/HirePanelCustomFields.vue'
import HirePanelFact from '@/components/Hire/HirePanelFact.vue'
import { graphql } from '@/gql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { getAvatarPath } from '@/utils/avatar'
import {
  getHireContractStatus,
  locationPreferenceLabel,
} from '@/utils/hireContract'

const props = defineProps<{
  hireId: string
  customFieldsAnsweredBy?: HireCustomFieldsAnsweredBy
}>()

const { result, loading, error } = useQuery(
  graphql(`
    query HirePanelOverview($id: ID!) {
      hire(id: $id) {
        id
        activeStatus
        pendingContractChanges
        endClient {
          id
          name
          avatar
        }
        ...HireCurrentContract
        ...HireEngagementColumn
      }
    }
  `),
  () => ({ id: props.hireId }),
  { clientId: 'publicApi' },
)

const hire = computed(() => result.value?.hire ?? null)

const contract = useHireContract(hire)

const contractStatus = computed(() =>
  getHireContractStatus(hire.value?.activeStatus, hire.value?.pendingContractChanges),
)
</script>
